import { useApolloClient } from '@apollo/client';
import { gql as sub } from '@apollo/client';
import moment from 'moment';
import React, { ReactNode, useEffect, useState } from 'react';

import { ActivityUser, useRoomLiveActivitySubscription } from '../hooks/useListingSubscription';
import {
  CreateRoomMessageInput,
  MessagesDocument,
  MessagesQuery,
  RoomType,
  useCreateMessageMutation,
  useMessagesQuery,
} from '../types/api.graphql';

export interface RoomDataType {
  id?: string;
  receiverId?: string;
  listingId?: string;
  parentListingId?: string;
  type?: RoomType;
}

type MessagesProviderProps = {
  children: ReactNode;
  senderId?: string;
  vehicleId?: string;
  receiverOrgId?: string;
  onLoadMessages?: () => void;
  receiverType?: string;
  roomData?: RoomDataType;
};

export interface MessageNode {
  messages?: {
    totalCount: number;
    nodes?: Array<{
      createdAt: any;
      id: string;
      receiverId?: string;
      receiverType?: string;
      sentAt: any;
      text: string;
      room?: {
        type?: string;
        ownerOrganization?: {
          id: string;
        };
      };
      sender: {
        id: string;
        firstName?: string;
        email?: string;
        lastName?: string;
        imageUrl?: string;
        organizations?: {
          id: string;
          name?: string;
        }[];
      };
    } | null> | null;
    pageInfo: {
      endCursor?: string | null;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor?: string | null;
    };
  };
}

interface MessagesProviderValuesTypes {
  loadingMessages: boolean;
  createInOrgDirectMessage: (...args: any) => Promise<any>;
  createInOrgGeneralMessage: (...args: any) => Promise<any>;
  createVehicleInternalMessage: (...args: any) => Promise<any>;
  createOrgInviteRoomMessage: (...args: any) => Promise<any>;
  createShareRoomMessage: (...args: any) => Promise<any>;
  createListingInternalMessage: (...args: any) => Promise<any>;
  reSendMessage: (sentAt: string) => Promise<any>;
  messages: () => MessageNode | null;
  creatingMessage: boolean;
  loadMoreMessages: () => void;
  hasMoreMessages: boolean;
  roomId?: string;
  newMessageArrived: boolean;
  setNewMessageArrived: (value: boolean) => void;
  isMessageSending: (sentAt: string) => boolean;
  isMessageError: (sentAt: string) => boolean;
  usersActivity: ActivityUser[];
}

export const MessagesContext = React.createContext<MessagesProviderValuesTypes>(undefined as any);

export const MessagesSubscriptionGQL = sub`
subscription MessageCreatedSubscription {
  messageCreated {
    createdAt
    id
    receiverId
    receiverType
    room {
      listingId
      type
      createdAt
      id
      name
    }
    sender {
      id
      firstName
      email
      lastName
      imageUrl
      organizations {
        id
        name
        imageUrl
      }
    }
    sentAt
    text
    senderOrganizationId
  }
}
`;

export const MessagesProvider = ({ children, onLoadMessages, senderId, roomData }: MessagesProviderProps) => {
  const { cache } = useApolloClient();

  const [roomId, setRoomId] = useState<string | undefined>(roomData?.id);
  const [receiverId, setReceiverId] = useState<string | undefined>(roomData?.receiverId);
  const [newMessageArrived, setNewMessageArrived] = useState(false);
  const [sendingMessageDates, setSendingMessageDates] = useState<string[]>([]);
  const [errorMessageDates, setErrorMessageDates] = useState<string[]>([]);
  const [usersActivity, setUsersActivity] = useState<ActivityUser[]>(undefined);

  const unsubscribeRef = React.useRef<any>(undefined);

  // Subscriptinos
  useRoomLiveActivitySubscription(roomData, setUsersActivity);

  const [createMessageMutation, { loading: creatingMessage }] = useCreateMessageMutation({
    onCompleted: (data) => {
      if (!data) return;
      const roomIdentifier = data?.createMessage.room.id;
      if (!roomId && roomIdentifier) {
        setRoomId(roomIdentifier);
      }

      setSendingMessageDates(sendingMessageDates?.filter((date) => date !== data.createMessage?.sentAt));
      removeErrorFromMessageBySentAt(data.createMessage?.sentAt);
    },
  });

  const getMessagesFromCache = (messageRoomId?: string): MessagesQuery =>
    cache.readQuery({ query: MessagesDocument, variables: { roomId: messageRoomId ?? roomId } });

  const removeErrorFromMessageBySentAt = (sentAt: string) => {
    setErrorMessageDates(errorMessageDates?.filter((date) => date !== sentAt));
  };

  const messages = () => {
    const messagesFromCache = getMessagesFromCache();
    return roomId ? messagesFromCache || messagesData : undefined;
  };

  const isMessageSending = (sentAt: string) => {
    if (!sentAt) {
      return false;
    }
    return sendingMessageDates.includes(sentAt);
  };

  const isMessageError = (sentAt: string) => {
    if (!sentAt) {
      return false;
    }
    return errorMessageDates.includes(sentAt);
  };

  const reSendMessage = (sentAt: string) => {
    const cachedMessages = messages();
    const cachedMessage = cachedMessages?.messages?.nodes?.find((message) => message.sentAt === sentAt);
    const message = {
      sentAt: cachedMessage?.sentAt,
      text: cachedMessage?.text,
      roomId: roomId,
      roomType: cachedMessage?.room.type,
      receiverId: cachedMessage?.receiverId,
      listingId: cachedMessage?.room.listingId,
      senderOrganizationId: cachedMessage?.senderOrganizationId,
    };
    removeErrorFromMessageBySentAt(sentAt);
    return sendMessage?.(message);
  };

  const sanitizeMessage = (message: Partial<CreateRoomMessageInput>) => {
    return {
      createdAt: new Date(),
      id: '-1',
      receiverId: '-1',
      receiverType: '-1',
      room: {
        id: roomId,
        listingId: message?.listingId,
        type: message.roomType,
        __typename: 'Room',
      },
      sender: {
        id: senderId,
        __typename: 'User',
      },
      senderOrganizationId: '',
      ...message,
    };
  };

  const addMessageToCache = (message: Partial<CreateRoomMessageInput>, messageRoomId?: string) => {
    const cachedData = getMessagesFromCache(messageRoomId);
    const sanitizedMessage = sanitizeMessage(message);
    if (cachedData?.messages) {
      cache.writeQuery({
        query: MessagesDocument,
        variables: { roomId: messageRoomId ?? roomId },
        data: {
          messages: {
            nodes: [sanitizedMessage],
            pageInfo: { hasNextPage: false, endCursor: null, startCursor: null, hasPreviousPage: false },
            totalCount: 0,
          },
        },
      });
    }
  };

  const {
    data: messagesData,
    loading: loadingMessages,
    fetchMore,
    subscribeToMore,
  } = useMessagesQuery({
    variables: {
      roomId: roomId,
      first: 10,
    },
    onCompleted() {
      onLoadMessages?.();
    },
    fetchPolicy: 'cache-and-network',
    skip: !roomId,
  });

  const loadMoreMessages = () => {
    void (async () => {
      await fetchMore?.({
        variables: {
          after: messagesData?.messages.pageInfo?.endCursor,
        },
      });
    })();
  };

  const sentAt = moment().toISOString();

  const createMessage = (text: string) => ({
    sentAt: sentAt,
    text: text,
    roomId: roomId,
  });

  const sendMessage = async (message: CreateRoomMessageInput) => {
    const datesTemp = [...sendingMessageDates];
    datesTemp.push(message.sentAt);
    setSendingMessageDates(datesTemp);
    addMessageToCache(message);
    await createMessageMutation({
      variables: {
        input: message,
      },
      onError: () => {
        // remove sending opacity
        setSendingMessageDates(sendingMessageDates.filter((date) => date !== message?.sentAt));
        const auxErrorMessageDates = [...errorMessageDates];
        auxErrorMessageDates?.push(message.sentAt);
        setErrorMessageDates(auxErrorMessageDates);
      },
    });
  };

  const createInOrgDirectMessage = (text: string) => {
    const message = {
      ...createMessage(text),
      roomType: RoomType.InOrgDirectRoom,
      receiverId: receiverId,
    };
    return sendMessage(message);
  };

  const createInOrgGeneralMessage = (text: string) => {
    const message = {
      ...createMessage(text),
      roomType: RoomType.InOrgGeneralRoom,
    };
    return sendMessage(message);
  };

  const createListingInternalMessage = (text: string, listingId: string, senderOrganizationId?: string) => {
    const message = {
      ...createMessage(text),
      roomType: RoomType.ListingInternalRoom,
      listingId: listingId,
      senderOrganizationId: senderOrganizationId,
    };
    return sendMessage(message);
  };

  const createShareRoomMessage = (text: string, listingId: string, senderOrganizationId?: string) => {
    const message = {
      sentAt: sentAt,
      text: text,
      roomType: RoomType.ShareRoom,
      roomId: roomId,
      receiverId: receiverId ?? '',
      listingId: listingId,
      senderOrganizationId: senderOrganizationId,
    };
    return sendMessage(message);
  };

  const createVehicleInternalMessage = (text: string, vehicleId: string) => {
    const message = {
      ...createMessage(text),
      roomType: RoomType.ListingInternalRoom,
      vehicleId: vehicleId,
    };
    return sendMessage(message);
  };

  const createOrgInviteRoomMessage = (text: string, vehicleId: string) => {
    const message = {
      ...createMessage(text),
      roomType: RoomType.OrgInviteRoom,
      receiverId: receiverId,
      vehicleId: vehicleId,
    };
    return sendMessage(message);
  };

  useEffect(() => {
    if (roomData?.receiverId !== receiverId) {
      setReceiverId(roomData?.receiverId);
      setRoomId(roomData?.id);
    } else {
      setRoomId(roomData?.id);
    }
  }, [roomData, receiverId]);

  const clearMessagesCache = () => {
    cache.evict({ id: 'ROOT_QUERY', fieldName: 'messages' });
    cache.gc();
  };

  useEffect(() => {
    return () => {
      clearMessagesCache();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!subscribeToMore) return null;
    unsubscribeRef.current?.();
    unsubscribeRef.current = subscribeToMore({
      document: MessagesSubscriptionGQL,
      updateQuery: (_: any, { subscriptionData }: any) => {
        const newMessage = subscriptionData?.data?.messageCreated;
        const messageRoomId = newMessage?.room?.id;
        addMessageToCache(newMessage, messageRoomId);
        setNewMessageArrived(true);
        return undefined;
      },
      onError: () => {
        return;
      },
    });
    return () => {
      unsubscribeRef.current?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeToMore, messagesData]);

  return (
    <MessagesContext.Provider
      value={{
        createInOrgDirectMessage,
        createInOrgGeneralMessage,
        createVehicleInternalMessage,
        createOrgInviteRoomMessage,
        createShareRoomMessage,
        createListingInternalMessage,
        creatingMessage,
        messages,
        loadingMessages,
        loadMoreMessages,
        hasMoreMessages: messagesData?.messages?.pageInfo?.hasNextPage ?? false,
        newMessageArrived,
        setNewMessageArrived,
        roomId,
        isMessageSending,
        isMessageError,
        reSendMessage,
        usersActivity,
      }}
    >
      {children}
    </MessagesContext.Provider>
  );
};

export function useMessagesContext() {
  return React.useContext(MessagesContext);
}
